import { Provider } from '@angular/core';
import { injectDispatch } from '@ngrx/signals/events';
import { StoreCoordinationEvents } from '@app/domain/events';
import { AccountDomainService } from '@app/domain/services';
import {
  GetAccountsUseCase,
  GetAccountByIdUseCase,
  GetAccountBalanceUseCase,
  CreateAccountUseCase,
  UpdateAccountUseCase,
  DeleteAccountUseCase,
  ReorderAccountsUseCase,
  FindInitialBalancesUseCase,
  UpsertInitialBalanceUseCase,
  MergeAccountsUseCase,
} from '@app/domain/use-cases';
import type {
  IAccountRepository,
  IExpenseRepository,
  IIncomeRepository,
  IPlannedExpenseRepository,
  IAccountTransferRepository,
  ICurrencyExchangeRepository,
  IDebtorRepository,
  IDebtorPaymentRepository,
  ICreditorRepository,
  ICreditorPaymentRepository,
  ITransactionManager,
} from '@app/domain/ports';
import {
  ACCOUNT_REPOSITORY,
  EXPENSE_REPOSITORY,
  INCOME_REPOSITORY,
  PLANNED_EXPENSE_REPOSITORY,
  ACCOUNT_TRANSFER_REPOSITORY,
  CURRENCY_EXCHANGE_REPOSITORY,
  DEBTOR_REPOSITORY,
  DEBTOR_PAYMENT_REPOSITORY,
  CREDITOR_REPOSITORY,
  CREDITOR_PAYMENT_REPOSITORY,
  TRANSACTION_MANAGER,
} from '../../tokens/infrastructure.tokens';

/**
 * Provides Account domain services and use cases.
 *
 * Includes:
 * - AccountDomainService: Business rules and validation
 * - 9 Use Cases: GetAccounts, GetAccountById, GetAccountBalance, Create, Update, Delete, Reorder, FindInitialBalances, UpsertInitialBalance
 *
 * All providers use constructor injection pattern following Clean Architecture.
 */
export function provideAccountDomain(): Provider[] {
  return [
    // Domain Service
    {
      provide: AccountDomainService,
      useFactory: () => new AccountDomainService(),
      deps: [],
    },

    // Use Cases - Query
    {
      provide: GetAccountsUseCase,
      useFactory: (repository: IAccountRepository) => new GetAccountsUseCase(repository),
      deps: [ACCOUNT_REPOSITORY],
    },
    {
      provide: GetAccountByIdUseCase,
      useFactory: (repository: IAccountRepository) => new GetAccountByIdUseCase(repository),
      deps: [ACCOUNT_REPOSITORY],
    },
    {
      provide: GetAccountBalanceUseCase,
      useFactory: (repository: IAccountRepository) => new GetAccountBalanceUseCase(repository),
      deps: [ACCOUNT_REPOSITORY],
    },
    {
      provide: FindInitialBalancesUseCase,
      useFactory: (repository: IAccountRepository) => new FindInitialBalancesUseCase(repository),
      deps: [ACCOUNT_REPOSITORY],
    },

    // Use Cases - Commands
    {
      provide: CreateAccountUseCase,
      useFactory: (repository: IAccountRepository, domainService: AccountDomainService) => {
        const dispatch = injectDispatch(StoreCoordinationEvents);
        return new CreateAccountUseCase(repository, domainService, dispatch);
      },
      deps: [ACCOUNT_REPOSITORY, AccountDomainService],
    },
    {
      provide: UpdateAccountUseCase,
      useFactory: (repository: IAccountRepository, domainService: AccountDomainService) => {
        const dispatch = injectDispatch(StoreCoordinationEvents);
        return new UpdateAccountUseCase(repository, domainService, dispatch);
      },
      deps: [ACCOUNT_REPOSITORY, AccountDomainService],
    },
    {
      provide: DeleteAccountUseCase,
      useFactory: (repository: IAccountRepository) => {
        const dispatch = injectDispatch(StoreCoordinationEvents);
        return new DeleteAccountUseCase(repository, dispatch);
      },
      deps: [ACCOUNT_REPOSITORY],
    },
    {
      provide: ReorderAccountsUseCase,
      useFactory: (repository: IAccountRepository) => {
        const dispatch = injectDispatch(StoreCoordinationEvents);
        return new ReorderAccountsUseCase(repository, dispatch);
      },
      deps: [ACCOUNT_REPOSITORY],
    },
    {
      provide: UpsertInitialBalanceUseCase,
      useFactory: (repository: IAccountRepository) => {
        const dispatch = injectDispatch(StoreCoordinationEvents);
        return new UpsertInitialBalanceUseCase(repository, dispatch);
      },
      deps: [ACCOUNT_REPOSITORY],
    },
    {
      provide: MergeAccountsUseCase,
      useFactory: (
        accountRepo: IAccountRepository,
        expenseRepo: IExpenseRepository,
        incomeRepo: IIncomeRepository,
        plannedExpenseRepo: IPlannedExpenseRepository,
        transferRepo: IAccountTransferRepository,
        exchangeRepo: ICurrencyExchangeRepository,
        debtorRepo: IDebtorRepository,
        debtorPaymentRepo: IDebtorPaymentRepository,
        creditorRepo: ICreditorRepository,
        creditorPaymentRepo: ICreditorPaymentRepository,
        txManager: ITransactionManager,
      ) => {
        const dispatch = injectDispatch(StoreCoordinationEvents);
        return new MergeAccountsUseCase(
          accountRepo,
          expenseRepo,
          incomeRepo,
          plannedExpenseRepo,
          transferRepo,
          exchangeRepo,
          debtorRepo,
          debtorPaymentRepo,
          creditorRepo,
          creditorPaymentRepo,
          txManager,
          dispatch,
        );
      },
      deps: [
        ACCOUNT_REPOSITORY,
        EXPENSE_REPOSITORY,
        INCOME_REPOSITORY,
        PLANNED_EXPENSE_REPOSITORY,
        ACCOUNT_TRANSFER_REPOSITORY,
        CURRENCY_EXCHANGE_REPOSITORY,
        DEBTOR_REPOSITORY,
        DEBTOR_PAYMENT_REPOSITORY,
        CREDITOR_REPOSITORY,
        CREDITOR_PAYMENT_REPOSITORY,
        TRANSACTION_MANAGER,
      ],
    },
  ];
}
